/* eslint-disable react/prop-types */
import React, {useState, useEffect} from 'react';
import {useMediaQuery} from 'react-responsive';
import {Link} from 'react-router-dom';
import {Button, Fab, Drawer} from '@mui/material';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import MenuIcon from '@mui/icons-material/Menu';
import styled, {keyframes} from 'styled-components';
import Tab from '../../Components/tab';
import Footer from '../../Components/Footer';
import logo from '../../Assets/logo.png';
import fotoRetrato from '../../Assets/historiaCasamentoRetrato.png';
import fotoDanca from '../../Assets/historiaCasamentoDanca.png';
import fotoCarro from '../../Assets/historiaPrimeiroCarro.png';
import fotoProgramador from '../../Assets/historiaProgramador.png';
import fotoDorDeCabeca from '../../Assets/historiaDorDeCabeca.png';
import fotoChile from '../../Assets/historiaChile.png';

/**
 * Página da história do Fiinc (ireit.com.br/financas/historia) — item 2 do
 * plano em docs/FIINC_LANDING_SITE.md (enterpriseireit). Textos vêm do
 * relato real do usuário (2026-09-02, também salvo em
 * docs/FIINC_LANDING_COPY.md) — não mexer no conteúdo sem checar lá.
 *
 * Layout "diário" (pedido explícito do usuário, 2026-09-02): a versão em
 * timeline (linha vertical + textinho estreito no centro) desperdiçava
 * espaço de tela, principalmente no desktop. Virou uma sequência de cards
 * largos com fundo pastel — os 3 capítulos com foto (`CAPITULOS_GRANDES`),
 * os 3 sem foto (`CAPITULOS_PEQUENOS`) num grid de 3 colunas. Empilha tudo
 * em coluna única no mobile.
 *
 * Dentro de `CAPITULOS_GRANDES`, cada capítulo escolhe seu próprio layout
 * (2026-09-03, iterado sessão por sessão com o usuário): `CardCompacto`
 * pra 1 parágrafo + 1 foto lado a lado com altura compacta (~200-400px,
 * não uma página inteira) — "Quem eu sou" e "O casamento sem reserva" (essa
 * última invertida, foto à esquerda); `CardMultiFoto` pra quando o capítulo
 * tem mais de um bloco parágrafo+foto sob o mesmo título, alternando o lado
 * da foto a cada bloco — usado em "O carro que devia resolver, e não
 * resolveu".
 *
 * Fotos reais (geradas por IA a partir de fotos do usuário, pra não expor o
 * rosto real dele/da esposa — pedido explícito) em `src/Assets/historia*.png`
 * (sem `_` no nome — HostGator não suporta)
 * — ver comentário de recorte na sessão de 2026-09-02 se precisar refazer.
 *
 * 2026-09-03: revertido de propósito pra este estado (pedido explícito do
 * usuário: "desfaça as mudanças e deixa como estava ontem, desconsidere o
 * que a gente fez da página de história hj") — 3 tentativas nesse dia
 * (caixa fixa com crop, blocos com flexbox stretch, grid) não agradaram.
 * Histórico de tudo que foi tentado e desfeito guardado na memória
 * (docs/FIINC_LANDING_SITE.md / memória do agente), caso sirva de ponto de
 * partida numa sessão futura.
 */

const fadeUp = keyframes`
  from { transform: translateY(18px); opacity: 0; }
  to   { transform: translateY(0);    opacity: 1; }
`;
const FadeUp = styled.div`
  animation: ${fadeUp} 0.55s ease-out both;
  animation-delay: ${p => p.delay || '0s'};
`;

const C = {
  azul: '#06448e',
  azulD: '#04316a',
  laranja: '#FF6600',
  laranjaD: '#cc4400',
  text: '#1a1a2e',
  muted: '#6b7280',
  bg: '#f4f6fa',
  white: '#ffffff',
};

const NAV_HEIGHT = 60;

const Paragrafo = ({children, ultimo}) => (
  <p
    style={{
      fontSize: 15.5,
      color: '#3a3a3a',
      lineHeight: 1.8,
      margin: ultimo ? 0 : '0 0 14px',
    }}
  >
    {children}
  </p>
);

const Quote = ({children, destaque, cor}) => (
  <blockquote
    style={
      destaque
        ? {
            margin: '18px 0',
            padding: '16px 20px',
            background: '#fdecef',
            border: '1px solid #f6c9d1',
            borderRadius: 14,
          }
        : {
            margin: '18px 0',
            padding: '4px 0 4px 18px',
            borderLeft: `3px solid ${cor || C.laranja}`,
          }
    }
  >
    <p
      style={{
        fontSize: destaque ? 16 : 17,
        fontWeight: 700,
        color: destaque ? '#b3364a' : C.text,
        lineHeight: 1.5,
        margin: 0,
        fontStyle: destaque ? 'normal' : 'italic',
        textAlign: destaque ? 'center' : 'left',
      }}
    >
      &quot;{children}&quot;
    </p>
  </blockquote>
);

const IconeBadge = ({children, tamanho}) => (
  <div
    style={{
      width: tamanho || 52,
      height: tamanho || 52,
      borderRadius: '50%',
      background: C.white,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontSize: (tamanho || 52) * 0.45,
      marginBottom: 16,
      boxShadow: '0 4px 14px rgba(20,30,60,0.10)',
      flexShrink: 0,
    }}
  >
    {children}
  </div>
);

// ─── Capítulos com foto — cards largos, duas colunas ───────────────────────
const CAPITULOS_GRANDES = [
  {
    icone: '👨‍💻',
    titulo: 'Quem eu sou',
    corFundo: '#eef3fa',
    corpo: (
      <Paragrafo ultimo>
        Sou o João, desenvolvedor de software há 9 anos. Boa parte desse tempo
        eu dediquei a construir um super app de mobilidade, delivery e entregas
        — do zero até o lançamento.
      </Paragrafo>
    ),
    foto: {src: fotoProgramador, legenda: 'João no computador'},
  },
  {
    icone: '💍',
    titulo: 'O casamento sem reserva',
    corFundo: '#fdf1e6',
    corpo: (
      <>
        <Paragrafo>
          Um ano depois de lançar, eu me casei. Minha esposa tinha acabado de se
          formar. Pra gente morar junto, o casamento era o caminho — e, como
          todo casal, encaramos sem ter uma reserva pronta pra pagar a festa.
        </Paragrafo>
        <Paragrafo ultimo>
          Foram meses intensos, com muito esforço dela e o que eu conseguia
          tirar do trabalho. Mas conseguimos. Foi lindo, e foi difícil — as duas
          coisas ao mesmo tempo.
        </Paragrafo>
      </>
    ),
    foto: {src: fotoDanca, legenda: 'Nosso casamento', foco: 'center 30%'},
  },
  {
    icone: '🚗',
    titulo: 'O carro que devia resolver, e não resolveu',
    corFundo: '#e7f0fb',
    blocos: [
      {
        corpo: (
          <>
            <Paragrafo>
              Meses depois, sentimos o preço de andar de aplicativo o tempo todo
              — mobilidade urbana, transporte, tudo somado pesava demais no fim
              do mês. Depois de muita conta na ponta do lápis e algumas noites
              sem dormir, conseguimos planejar, visitar concessionárias e
              comprar nosso primeiro carro.
            </Paragrafo>
            <Paragrafo ultimo>Achamos que resolvia.</Paragrafo>
          </>
        ),
        foto: {
          src: fotoCarro,
          legenda: 'Nosso primeiro carro',
          foco: 'center 35%',
        },
      },
      {
        corpo: (
          <>
            <Paragrafo>
              Dois meses depois, as contas estavam cada vez mais difíceis de
              fechar. A gente sentava, tentava entender de onde vinham os
              gastos, e não conseguia — só sabíamos que estávamos vivendo pra
              pagar contas, sem enxergar o quadro todo.
            </Paragrafo>
            <Quote cor={C.azul}>
              Estávamos vivendo pra pagar contas, sem enxergar o quadro todo.
            </Quote>
          </>
        ),
        foto: {
          src: fotoDorDeCabeca,
          legenda: 'Tentando entender pra onde ia o dinheiro',
        },
      },
    ],
  },
];

// ─── Capítulos sem foto — grid de 3 colunas ─────────────────────────────────
const CAPITULOS_PEQUENOS = [
  {
    icone: '💡',
    titulo: 'A virada',
    corFundo: '#eafaf0',
    corpo: (
      <>
        <Paragrafo>
          Foi aí que usei a mesma estrutura que já tinha construído pros meus
          outros apps pra montar uma ferramenta só nossa: um jeito de colocar
          nossas finanças na ponta do lápis de verdade.
        </Paragrafo>
        <Paragrafo ultimo>
          Funcionou. Descobrimos onde estávamos gastando demais, conseguimos
          organizar as dívidas, e pela primeira vez em muito tempo sentimos que
          tínhamos controle — não só sorte.
        </Paragrafo>
      </>
    ),
  },
  {
    icone: '🧠',
    titulo: 'De onde vem o nome',
    corFundo: '#f3eefc',
    corpo: (
      <Paragrafo ultimo>
        Hoje esse app é o <b>Irê it Finanças</b>, carinhosamente chamado de{' '}
        <b>Fiinc</b> — nosso assistente de IA lá dentro. É um trocadilho com{' '}
        <i>think</i>, pensar, mais os dois &quot;i&quot; de Irê It e o
        &quot;finc&quot; de finanças.
      </Paragrafo>
    ),
  },
];

// ─── Capítulo final — sessão sozinha, sem ícone, com foto (2026-09-03) ─────
const CAPITULO_COMUNIDADE = {
  titulo: 'Por que agora é de todo mundo',
  corFundo: '#fdecef',
  corpo: (
    <>
      <Paragrafo>
        Estou disponibilizando o Fiinc pra outras pessoas que passam pela mesma
        dificuldade — porque:
      </Paragrafo>
      <Quote destaque>
        Organizar as finanças não devia ser um privilégio de quem já entende de
        planilha.
      </Quote>
      <Paragrafo ultimo>
        É o primeiro passo pra realizar os sonhos que a gente quiser: uma casa,
        uma viagem, ou simplesmente chegar na aposentadoria com tranquilidade.
        Se vocês estão passando pelo mesmo aperto que a gente passou, espero que
        o Fiinc ajude do mesmo jeito que ajudou a nossa família.
      </Paragrafo>
    </>
  ),
  foto: {src: fotoChile, legenda: 'Nossa viagem'},
};

// ─── Card com vários blocos de parágrafo+foto sob um único título ─────────
const CardMultiFoto = ({cap, mobile}) => (
  <div
    style={{
      background: cap.corFundo,
      borderRadius: 24,
      padding: mobile ? '28px 24px' : '40px 44px',
      marginBottom: 24,
    }}
  >
    <h2
      style={{
        fontSize: mobile ? 19 : 22,
        fontWeight: 800,
        color: C.text,
        margin: '0 0 20px',
      }}
    >
      {cap.titulo}
    </h2>
    {cap.blocos.map((bloco, i) => (
      <div
        key={i}
        style={{
          display: 'flex',
          flexDirection: mobile
            ? 'column'
            : i % 2 === 1
            ? 'row-reverse'
            : 'row',
          alignItems: 'center',
          gap: mobile ? 20 : 32,
          marginTop: i === 0 ? 0 : 28,
        }}
      >
        <div style={{flex: 1, minWidth: 0}}>{bloco.corpo}</div>
        <div
          style={{
            flex: mobile ? 'unset' : '0 0 300px',
            width: mobile ? '100%' : undefined,
            height: mobile ? 260 : 280,
            borderRadius: 16,
            overflow: 'hidden',
          }}
        >
          <img
            src={bloco.foto.src}
            alt={bloco.foto.legenda}
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              objectPosition: bloco.foto.foco || 'center',
              display: 'block',
            }}
          />
        </div>
      </div>
    ))}
  </div>
);

// ─── Card único do "Quem eu sou" — foto do tamanho exato do texto (desktop)
// e banner cortado ocupando o width todo (mobile) ─────────────────────────
const CardCompacto = ({cap, mobile, invertido}) => (
  <div
    style={{
      display: 'flex',
      flexDirection: mobile ? 'column' : invertido ? 'row-reverse' : 'row',
      alignItems: 'stretch',
      background: cap.corFundo,
      borderRadius: 24,
      overflow: 'hidden',
      marginBottom: 24,
    }}
  >
    <div
      style={{
        flex: 1,
        padding: mobile ? '28px 24px' : '40px 44px',
        minWidth: 0,
      }}
    >
      <h2
        style={{
          fontSize: mobile ? 19 : 22,
          fontWeight: 800,
          color: C.text,
          margin: '0 0 12px',
        }}
      >
        {cap.titulo}
      </h2>
      {cap.corpo}
    </div>
    <div
      style={{
        flex: mobile ? 'unset' : '0 0 380px',
        width: mobile ? '100%' : undefined,
        height: mobile ? 400 : 400,
      }}
    >
      <img
        src={cap.foto.src}
        alt={cap.foto.legenda}
        style={{
          width: '100%',
          height: '100%',
          objectFit: 'cover',
          objectPosition: cap.foto.foco || 'center 45%',
          display: 'block',
        }}
      />
    </div>
  </div>
);

const CardPequeno = ({cap, mobile}) => (
  <div
    style={{
      background: cap.corFundo,
      borderRadius: 24,
      padding: mobile ? '26px 22px' : '32px 28px',
      flex: mobile ? 'unset' : 1,
    }}
  >
    <IconeBadge tamanho={46}>{cap.icone}</IconeBadge>
    <h2
      style={{
        fontSize: 18,
        fontWeight: 800,
        color: C.text,
        margin: '0 0 12px',
      }}
    >
      {cap.titulo}
    </h2>
    {cap.corpo}
  </div>
);

const FinancasHistoria = () => {
  const isSmall = useMediaQuery({query: '(max-width: 750.98px)'});
  const isVerySmall = useMediaQuery({query: '(max-width:450.00px)'});
  const isDesktopEstreito = useMediaQuery({query: '(max-width: 980px)'});
  const mobile = isSmall || isVerySmall;
  const [toggleBar, setToggleBar] = useState(false);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    window.scrollTo({top: 0, behavior: 'smooth'});
  }, []);

  useEffect(() => {
    const t = () => setIsVisible(window.pageYOffset > 50);
    window.addEventListener('scroll', t);
    return () => window.removeEventListener('scroll', t);
  }, []);

  const textoHero = (
    <>
      <p
        style={{
          color: C.laranja,
          fontWeight: 800,
          fontSize: 13,
          letterSpacing: 1,
          textTransform: 'uppercase',
          margin: '0 0 10px',
        }}
      >
        Por que existe o Fiinc
      </p>
      <h1
        style={{
          color: C.text,
          fontSize: mobile ? 30 : 42,
          fontWeight: 800,
          margin: '0 0 18px',
          lineHeight: 1.15,
        }}
      >
        A gente não criou um app de finanças.
        <br />A gente resolveu um{' '}
        <span style={{color: C.laranja}}>problema nosso</span>.
      </h1>
      <p style={{fontSize: 16, color: C.muted, lineHeight: 1.7, margin: 0}}>
        A história de como o Fiinc nasceu — contada por quem viveu.
      </p>
    </>
  );

  return (
    <div style={{overflowX: 'hidden', width: '100%', background: C.white}}>
      {isVisible && (
        <Fab
          onClick={() => window.scrollTo({top: 0, behavior: 'smooth'})}
          style={{position: 'fixed', bottom: 20, left: 20, zIndex: 900}}
          size="small"
          color="primary"
          aria-label="voltar ao topo"
        >
          <ArrowUpwardIcon />
        </Fab>
      )}

      {mobile ? (
        <div
          style={{
            background: C.azul,
            height: NAV_HEIGHT,
            alignItems: 'center',
            display: 'flex',
            justifyContent: 'space-between',
            position: 'fixed',
            zIndex: 999,
            top: 0,
            left: 0,
            width: '100%',
          }}
        >
          <Button
            component={Link}
            to="/"
            style={{
              width: 95,
              height: 47,
              marginLeft: 12,
              backgroundImage: `url(${logo})`,
              backgroundPosition: 'center',
              backgroundRepeat: 'no-repeat',
              backgroundSize: 'cover',
            }}
          />
          <MenuIcon
            style={{color: '#ffffff', marginRight: 15}}
            onClick={() => setToggleBar(true)}
          />
        </div>
      ) : (
        <Tab />
      )}
      {mobile && (
        <Drawer
          anchor="top"
          open={toggleBar}
          onClose={() => setToggleBar(false)}
        >
          <Tab setToggleBar={setToggleBar} />
        </Drawer>
      )}

      <div
        style={{
          maxWidth: 1120,
          margin: '0 auto',
          padding: `${NAV_HEIGHT}px 22px 0`,
        }}
      ></div>

      {/* ─── Hero: foto colada na borda direita (capa, tipo Facebook no mobile) ─── */}
      <FadeUp
        style={{
          display: 'flex',
          flexDirection: mobile ? 'column' : 'row',
          alignItems: 'stretch',
          width: '100%',
          marginBottom: 48,
        }}
      >
        {!mobile && (
          <div
            style={{
              flex: 1,
              minWidth: 0,
              paddingLeft: 'calc(max(0px, (100vw - 1120px) / 2) + 22px)',
              paddingRight: 44,
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
            }}
          >
            {textoHero}
          </div>
        )}

        <div
          style={{
            position: 'relative',
            flex: mobile ? 'unset' : '0 0 39%',
            width: mobile ? '100%' : undefined,
            height: mobile ? 390 : `calc(100vh - ${NAV_HEIGHT + 0}px)`,
            overflow: 'hidden',
          }}
        >
          {mobile && (
            <Link
              to="/financas"
              style={{
                position: 'absolute',
                top: 16,
                left: 16,
                zIndex: 2,
                display: 'inline-flex',
                alignItems: 'center',
                gap: 6,
                background: 'rgba(10,20,40,0.45)',
                backdropFilter: 'blur(4px)',
                color: '#ffffff',
                fontSize: 13,
                fontWeight: 700,
                textDecoration: 'none',
                padding: '7px 14px 7px 10px',
                borderRadius: 999,
              }}
            >
              <ArrowBackIcon style={{fontSize: 16}} /> Voltar pro Fiinc
            </Link>
          )}
          <img
            src={fotoRetrato}
            alt="João e sua esposa"
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              objectPosition: mobile ? 'center 20%' : 'center 30%',
              display: 'block',
            }}
          />
        </div>

        {mobile && <div style={{padding: '28px 22px 0'}}>{textoHero}</div>}
      </FadeUp>

      <div
        style={{
          maxWidth: 1120,
          margin: '0 auto',
          padding: '0 22px 70px',
        }}
      >
        {/* ─── Capítulos com foto ────────────────────────────────────────── */}
        {CAPITULOS_GRANDES.map((cap, i) =>
          cap.blocos ? (
            <FadeUp key={cap.titulo} delay={`${i * 0.08}s`}>
              <CardMultiFoto cap={cap} mobile={mobile} />
            </FadeUp>
          ) : (
            <FadeUp key={cap.titulo} delay={`${i * 0.08}s`}>
              <CardCompacto cap={cap} mobile={mobile} invertido={i === 1} />
            </FadeUp>
          ),
        )}

        {/* ─── Capítulos sem foto — lado a lado no desktop ────────────────── */}
        <FadeUp
          delay="0.24s"
          style={{
            display: 'flex',
            flexDirection: isDesktopEstreito ? 'column' : 'row',
            gap: 20,
            marginBottom: 24,
          }}
        >
          {CAPITULOS_PEQUENOS.map(cap => (
            <CardPequeno
              key={cap.titulo}
              cap={cap}
              mobile={isDesktopEstreito}
            />
          ))}
        </FadeUp>

        {/* ─── Capítulo final — sessão sozinha, com foto ──────────────────── */}
        <FadeUp delay="0.32s">
          <CardCompacto cap={CAPITULO_COMUNIDADE} mobile={mobile} />
        </FadeUp>

        <p
          style={{
            fontSize: 14,
            color: C.muted,
            fontWeight: 700,
            textAlign: 'right',
            margin: '18px 0 50px',
          }}
        >
          — João, criador do Irê it Finanças
        </p>

        {/* ─── CTA final ───────────────────────────────────────────────── */}
        <div
          style={{
            background: C.bg,
            borderRadius: 24,
            padding: '36px 24px',
            textAlign: 'center',
          }}
        >
          <p
            style={{
              fontSize: 19,
              fontWeight: 800,
              color: C.text,
              margin: '0 0 8px',
            }}
          >
            Comece hoje, o mesmo primeiro passo que a gente deu.
          </p>
          <p style={{fontSize: 14, color: C.muted, margin: '0 0 22px'}}>
            É de graça pra testar — sem cartão de crédito.
          </p>
          <div
            style={{
              display: 'flex',
              gap: 12,
              justifyContent: 'center',
              flexWrap: 'wrap',
            }}
          >
            <Button
              component={Link}
              to="/cadastro_financas"
              variant="contained"
              endIcon={<ArrowForwardIcon />}
              style={{
                textTransform: 'none',
                fontWeight: 700,
                fontSize: 15,
                padding: '12px 26px',
                borderRadius: 12,
              }}
              sx={{bgcolor: C.laranja, ':hover': {bgcolor: C.laranjaD}}}
            >
              Criar minha conta grátis
            </Button>
            <Button
              component={Link}
              to="/financas/demo"
              variant="outlined"
              style={{
                textTransform: 'none',
                fontWeight: 700,
                fontSize: 15,
                padding: '12px 26px',
                borderRadius: 12,
              }}
              sx={{color: C.azul, borderColor: C.azul}}
            >
              Ver como funciona
            </Button>
            <Button
              component={Link}
              to="/financas#planos"
              variant="text"
              style={{
                textTransform: 'none',
                fontWeight: 700,
                fontSize: 15,
                padding: '12px 26px',
                borderRadius: 12,
              }}
              sx={{color: C.azul}}
            >
              Conhecer os planos
            </Button>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default FinancasHistoria;
